import { pickDay } from './searchFilter'
import { NO_VIEW, type TableColumn, type TableFilters, type TableViewState } from './tableView'

/**
 * A list's column view in its address (TRKC-110): the sorted column as `order=<column>.<asc|desc>`
 * and each column's filter as `col.<column>=<value>`, beside the list's own filters — so going
 * back to it from a record, or opening a shared link, shows the same view, the rule the board
 * and the books already follow (TRKC-90, TRKC-97).
 *
 * <p>
 * An address can be typed by anyone, so every value is checked on the way in: a column the
 * table does not sort or filter, a choice its list does not offer, or a day that does not exist
 * is dropped. A figure that is not one is kept, and its box says so — the box's own rule.
 * </p>
 */

const ORDER = 'order'
const FILTER = 'col.'

/** The column view an address describes, every value checked. */
export function viewFromAddress<Row>(params: URLSearchParams, columns: readonly TableColumn<Row>[]): TableViewState {
  const order = params.get(ORDER)
  const [column, direction] = order?.split('.') ?? []
  const sortable = columns.find((candidate) => candidate.key === column && candidate.sortValue !== undefined)
  const sort =
    sortable !== undefined && (direction === 'asc' || direction === 'desc')
      ? { column: sortable.key, direction: direction === 'asc' ? ('ascending' as const) : ('descending' as const) }
      : null

  const filters: Record<string, string> = {}
  for (const candidate of columns) {
    const typed = params.get(FILTER + candidate.key)
    const filter = candidate.filter

    if (typed === null || typed.trim() === '' || filter === undefined) {
      continue
    }

    const kept =
      filter.kind === 'choice'
        ? filter.values.includes(typed)
          ? typed
          : undefined
        : filter.kind === 'onOrAfter'
          ? pickDay(typed)
          : filter.kind === 'atLeast' || filter.kind === 'atMost'
            ? // Kept as typed, readable or not: the box says "Not a number" for one that is not.
              typed.slice(0, 20)
            : typed.slice(0, 100)

    if (kept !== undefined) {
      filters[candidate.key] = kept
    }
  }

  return sort === null && Object.keys(filters).length === 0 ? NO_VIEW : { sort, filters }
}

/** The address with this view in it, and whatever else it said left as it was. */
export function withView(params: URLSearchParams, state: TableViewState): URLSearchParams {
  const next = new URLSearchParams(params)
  for (const key of [...next.keys()]) {
    if (key === ORDER || key.startsWith(FILTER)) {
      next.delete(key)
    }
  }

  if (state.sort !== null) {
    next.set(ORDER, `${state.sort.column}.${state.sort.direction === 'ascending' ? 'asc' : 'desc'}`)
  }

  for (const [column, typed] of Object.entries(state.filters as TableFilters)) {
    if (typed.trim() !== '') {
      next.set(FILTER + column, typed)
    }
  }

  return next
}

/**
 * A list's own filters written to its address, with the column view the address already held
 * carried along: writing the filters alone would throw the column view away.
 */
export function keepingView(next: URLSearchParams, current: URLSearchParams): URLSearchParams {
  const kept = new URLSearchParams(next)
  for (const [key, value] of current) {
    if (key === ORDER || key.startsWith(FILTER)) {
      kept.set(key, value)
    }
  }
  return kept
}
